import Colors from '@constants/Colors';
import { Box, Typography, Switch, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import VideoGalleryModal from './VideoGalleryModal';

const VideoGalleryContainer = styled(Box)(({ theme }) => ({
    backgroundColor: Colors.DASHBOARD,
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0px 4px 80px 0px rgba(112, 144, 176, 0.14)',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
}));

const VideoGalleryHeader = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
}));

const VideoTitle = styled(Typography)(() => ({
    fontFamily: 'LEMON_MILK_Pro, sans-serif',
    fontSize: '25px',
    fontWeight: 500,
    lineHeight: '40px',
    color: Colors.NEUTRAL_WHITE,
}));

const MainVideoContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    width: '100%',
    height: '198px',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '3.333px 3.333px 6.667px 0px rgba(0, 0, 0, 0.07)',
    cursor: 'pointer',
}));

const VideoElement = styled('video')(() => ({
    width: '100%',
    height: '100%',
    objectFit: 'cover',
}));

const VideoOverlay = styled(Box)<{ isPlaying?: boolean }>(({ isPlaying }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: isPlaying ? 'transparent' : 'rgba(0, 0, 0, 0.1)',
    pointerEvents: 'none',
}));

const PlayPauseButton = styled(IconButton)<{ isPlaying?: boolean }>(({ isPlaying }) => ({
    position: 'absolute',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(10px)',
    borderRadius: '59px',
    width: '76px',
    height: '76px',
    display: isPlaying ? 'none' : 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: Colors.NEUTRAL_BLACK,
    '&:hover': {
        backgroundColor: Colors.NEUTRAL_WHITE,
    },
}));

const ExpandButton = styled(IconButton)(() => ({
    position: 'absolute',
    top: '13px',
    right: '13px',
    color: Colors.NEUTRAL_WHITE,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    backdropFilter: 'blur(5px)',
    width: '32px',
    height: '32px',
    '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
}));

const ProgressBarContainer = styled(Box)(() => ({
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: '6px',
    backgroundColor: Colors.NEUTRAL_GRAY,
}));

const ProgressBar = styled(Box)<{ progress: number }>(({ progress }) => ({
    height: '100%',
    width: `${progress}%`,
    backgroundColor: Colors.PRIMARY_PURPLE,
    transition: 'width 0.1s ease',
}));

const ThumbnailContainer = styled(Box)(() => ({
    display: 'flex',
    gap: '14px',
    alignItems: 'center',
    width: '100%',
    position: 'relative',
}));

const VideoThumbnail = styled(Box)<{ isActive?: boolean }>(({ isActive }) => ({
    position: 'relative',
    width: '129px',
    height: '87px',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '3.333px 3.333px 6.667px 0px rgba(0, 0, 0, 0.07)',
    cursor: 'pointer',
    border: isActive ? `4px solid ${Colors.PRIMARY_PURPLE}` : 'none',
    '&:hover': {
        transform: 'scale(1.02)',
        transition: 'transform 0.2s ease',
    },
}));

const ThumbnailImage = styled('video')(() => ({
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
}));

const ThumbnailPlayIcon = styled(Box)<{ isActive?: boolean }>(({ isActive }) => ({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '24px',
    height: '24px',
    display: isActive ? 'none' : 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
}));

const NavigationButton = styled(IconButton)(() => ({
    position: 'absolute',
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: 'rgba(255, 255, 255, 0.6)',
    backdropFilter: 'blur(1.7px)',
    border: '1px solid rgba(255, 255, 255, 0.5)',
    borderRadius: '24px',
    width: '44px',
    height: '44px',
    color: Colors.NEUTRAL_BLACK,
    boxShadow: '0px 4px 10.1px 0px rgba(0, 0, 0, 0.25)',
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
    },
}));

const CustomSwitch = styled(Switch)(({ theme }) => ({
    '& .MuiSwitch-thumb': {
        backgroundColor: Colors.NEUTRAL_WHITE,
    },
    '& .MuiSwitch-track': {
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
    },
    '&.Mui-checked .MuiSwitch-track': {
        backgroundColor: Colors.PRIMARY_PURPLE,
    },
}));

const VideoGallery = ({ pro, jobOffer, onSubmit }: { pro: any, jobOffer: any, onSubmit?: () => Promise<void> }) => {
    const { setValue } = useFormContext();
    const [isEnabled, setIsEnabled] = useState(jobOffer?.showVideo ?? true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const videoRef = useRef<HTMLVideoElement>(null);

    // Get videos from brand first, fallback to pro
    const videos = useMemo(() => {
        const source = jobOffer?.brand || jobOffer?.pro || pro;
        const brandVideos = source?.brandVideos || [];
        return brandVideos.map((video: any, index: number) => ({
            id: index + 1,
            url: video.url,
            thumbnail: video.url, // Use video URL as thumbnail since we don't have separate thumbnails
            title: video.name || `Video ${index + 1}`,
            duration: 0 // Duration will be determined by video metadata
        })).filter((video: any) => video.url);
    }, [jobOffer, pro]);

    // Update toggle state when jobOffer changes
    useEffect(() => {
        if (jobOffer?.showVideo !== undefined) {
            setIsEnabled(jobOffer.showVideo);
        }
    }, [jobOffer?.showVideo]);

    // Handle toggle change and submit to backend
    const handleToggleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.checked;
        setIsEnabled(newValue);
        setValue('showVideo', newValue);
        await onSubmit?.();
    };

    const currentVideo = videos[currentVideoIndex];
    const visibleThumbnails = videos.slice(0, 3);

    const handlePlayPause = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const handleVideoClick = () => {
        setIsModalOpen(true);
    };

    const handleThumbnailClick = (index: number) => {
        if (index !== currentVideoIndex) {
            setCurrentVideoIndex(index);
            setIsPlaying(false);
            setCurrentTime(0);
        }
    };

    const handleExpandClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsModalOpen(true);
    };

    const handleTimeUpdate = () => {
        if (videoRef.current) {
            setCurrentTime(videoRef.current.currentTime);
        }
    };

    const handleLoadedMetadata = () => {
        if (videoRef.current) {
            setDuration(videoRef.current.duration);
        }
    };

    // Video event handlers with addEventListener for better control
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const handleLoadStart = () => setIsLoading(true);
        const handleCanPlay = () => setIsLoading(false);
        const handleLoadedMetadata = () => {
            setDuration(video.duration);
            setIsLoading(false);
        };
        const handleTimeUpdate = () => setCurrentTime(video.currentTime);
        const handlePlay = () => setIsPlaying(true);
        const handlePause = () => setIsPlaying(false);
        const handleEnded = () => setIsPlaying(false);

        video.addEventListener('loadstart', handleLoadStart);
        video.addEventListener('canplay', handleCanPlay);
        video.addEventListener('loadedmetadata', handleLoadedMetadata);
        video.addEventListener('timeupdate', handleTimeUpdate);
        video.addEventListener('play', handlePlay);
        video.addEventListener('pause', handlePause);
        video.addEventListener('ended', handleEnded);

        return () => {
            video.removeEventListener('loadstart', handleLoadStart);
            video.removeEventListener('canplay', handleCanPlay);
            video.removeEventListener('loadedmetadata', handleLoadedMetadata);
            video.removeEventListener('timeupdate', handleTimeUpdate);
            video.removeEventListener('play', handlePlay);
            video.removeEventListener('pause', handlePause);
            video.removeEventListener('ended', handleEnded);
        };
    }, [currentVideoIndex]);

    // Reset states when video changes
    useEffect(() => {
        setCurrentTime(0);
        setIsPlaying(false);
        setIsLoading(true);
        if (videoRef.current) {
            videoRef.current.currentTime = 0;
        }
    }, [currentVideoIndex]);

    const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

    // Don't render if no videos available
    if (!videos || videos.length === 0) {
        return null;
    }

    return (
        <>
            <VideoGalleryContainer>
                <VideoGalleryHeader>
                    <VideoTitle>Vidéos</VideoTitle>
                    <CustomSwitch
                        checked={isEnabled}
                        onChange={handleToggleChange}
                        size="medium"
                    />
                </VideoGalleryHeader>
                {isEnabled && (<>
                    {/* Main Video Player */}
                    <MainVideoContainer onClick={handleVideoClick}>
                        <VideoElement
                            ref={videoRef}
                            src={currentVideo.url}
                            preload="metadata"
                            playsInline
                            controls={false}
                            style={{ display: isLoading ? 'none' : 'block' }}
                        />

                        <VideoOverlay isPlaying={isPlaying}>
                            <PlayPauseButton
                                isPlaying={isPlaying}
                                onClick={handlePlayPause}
                            >
                                <i className="fi fi-sr-play" style={{ fontSize: '32px' }} />
                            </PlayPauseButton>
                        </VideoOverlay>

                        <ExpandButton onClick={handleExpandClick}>
                            <i className="fi fi-br-expand" style={{ fontSize: '19px' }} />
                        </ExpandButton>
                    </MainVideoContainer>

                    {/* Video Thumbnails */}
                    {videos.length > 1 && (
                        <ThumbnailContainer>
                            {visibleThumbnails.map((video, index) => (
                                <VideoThumbnail
                                    key={video.id}
                                    isActive={index === currentVideoIndex}
                                    onClick={() => handleThumbnailClick(index)}
                                >
                                    <ThumbnailImage
                                        src={video.url}
                                        preload="metadata"
                                        muted
                                        playsInline
                                        controls={false}
                                    />
                                    <ThumbnailPlayIcon isActive={index === currentVideoIndex}>
                                        <i className="fi fi-sr-play" style={{ fontSize: '24px', color: Colors.NEUTRAL_WHITE }} />
                                    </ThumbnailPlayIcon>
                                </VideoThumbnail>
                            ))}

                            {videos.length > 3 && (
                                <NavigationButton onClick={() => setIsModalOpen(true)}>
                                    <i className="fi fi-br-angle-small-right" style={{ fontSize: '24px' }} />
                                </NavigationButton>
                            )}
                        </ThumbnailContainer>
                    )}
                    </>)}
                </VideoGalleryContainer>

            <VideoGalleryModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                videos={videos}
                currentIndex={currentVideoIndex}
                onIndexChange={setCurrentVideoIndex}
            />
        </>
    );
};

export default VideoGallery;
